import { createContext, useContext, useState, useEffect } from "react";
import cartService from "./api/cartService";
import { createOrder, verifyPayment } from "./api/OrderService";
import { userStore } from "./store/userStore";

const CartContext = createContext({
  cart: [],
  addToCart: () => {},
  removeSingleItem: () => {},
  removeFromCart: () => {},
  setCart: () => {},
  getCart: () => {},
  clearCart: () => {},
  placeOrder: () => {},
});

// Global request counter to prevent spam
let requestCount = 0;
let lastRequestTime = 0;

export const CartProvider = ({ children }) => {
  console.log("🛒 CartProvider: Component mounted");
  const [cart, setCart] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const [currentRequest, setCurrentRequest] = useState(null);
  const userId = userStore((state) => state._id);
  
  console.log("🛒 CartProvider: Initial state - userId:", userId, "cart:", cart);

  // Initialize cart data when component mounts or user changes
  useEffect(() => {
    const initializeCart = async () => {
      console.log("🛒 CartProvider: useEffect triggered");
      console.log("🛒 CartProvider: userId =", userId);
      console.log("🛒 CartProvider: localStorage user-storage =", localStorage.getItem("user-storage"));
      
      // Add a small delay to ensure user state is fully loaded
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Check if user state is available after delay
      const currentUserId = userStore.getState()._id;
      console.log("🛒 CartProvider: currentUserId after delay =", currentUserId);
      
      if (currentUserId) {
        console.log("🛒 CartProvider: Initializing cart for user:", currentUserId);
        try {
          await getCartHandler({ userId: currentUserId });
        } catch (error) {
          console.error("🛒 CartProvider: Error initializing cart:", error);
        }
      } else {
        console.log("🛒 CartProvider: No user ID, checking localStorage for cart data");
        // Try to load from localStorage as fallback
        const localCart = localStorage.getItem("cart");
        console.log("🛒 CartProvider: localStorage cart =", localCart);
        if (localCart) {
          try {
            const parsedCart = JSON.parse(localCart);
            console.log("🛒 CartProvider: Loading cart from localStorage:", parsedCart);
            setCart(parsedCart);
          } catch (error) {
            console.error("🛒 CartProvider: Error parsing localStorage cart:", error);
          }
        } else {
          console.log("🛒 CartProvider: No cart data in localStorage");
        }
      }
    };

    initializeCart();
  }, [userId]);

  // Persist cart to localStorage whenever cart changes
  useEffect(() => {
    if (cart.length > 0) {
      localStorage.setItem("cart", JSON.stringify(cart));
      console.log("🛒 Cart changed, saved to localStorage:", cart);
    }
  }, [cart]);

  // Additional effect to handle delayed user state loading
  useEffect(() => {
    const checkUserAndLoadCart = () => {
      const currentUserId = userStore.getState()._id;
      console.log("🛒 CartProvider: Delayed check - currentUserId =", currentUserId);
      
      if (currentUserId && cart.length === 0) {
        console.log("🛒 CartProvider: User found, loading cart for:", currentUserId);
        getCartHandler({ userId: currentUserId });
      }
    };

    // Check after a longer delay to ensure user state is fully loaded
    const timeoutId = setTimeout(checkUserAndLoadCart, 500);
    
    return () => clearTimeout(timeoutId);
  }, []);

  const addToCartHandler = async (productObj) => {
    try {
      console.log("Adding to cart:", JSON.stringify(productObj, null, 2));
      const data = await cartService.addToCart(productObj.userId, productObj.productId, productObj.quantity);
      console.log("data after add to cart " + JSON.stringify(data, null, 2));
      
      // Force immediate cart refresh by resetting throttle
      setLastFetchTime(0);
      setIsLoading(false);
      
      // Refresh cart data from backend (this will update localStorage correctly)
      await getCartHandler({ userId: productObj.userId });
    } catch (error) {
      console.error("Error adding to cart:", error);
      alert("Failed to add item to cart. Please try again.");
    }
  };

  const removeFromCartHandler = async (userObj) => {
    const data = await cartService.removeFromCart(userObj.userId, userObj.productId);
    console.log("data after remove from cart " + JSON.stringify(data, null, 2));
    
    // Force immediate cart refresh
    setLastFetchTime(0);
    setIsLoading(false);
    getCartHandler(userObj);
  };

  const removeSingleItemHandler = async (userObj) => {
    const data = await cartService.removeFromCart(userObj.userId, userObj.productId);
    console.log(
      "data after remove single cart " + JSON.stringify(data, null, 2)
    );
    
    // Force immediate cart refresh
    setLastFetchTime(0);
    setIsLoading(false);
    getCartHandler(userObj);
  };

  const getCartHandler = async (userObj) => {
    console.log("🛒 getCartHandler called with:", userObj);
    const now = Date.now();
    const timeSinceLastFetch = now - lastFetchTime;
    
    // Prevent multiple requests within 1 second (reduced from 3 seconds)
    if (isLoading || timeSinceLastFetch < 1000) {
      console.log("🛒 Cart request blocked - too frequent or already in progress");
      return;
    }

    console.log("🛒 Making API call to get cart for user:", userObj.userId);
    setIsLoading(true);
    setLastFetchTime(now);
    
    try {
      const data = await cartService.getCart(userObj.userId);
      console.log("🛒 API response received:", JSON.stringify(data, null, 2));
      
      let dataItems = [];

      if (data?.items?.length > 0) {
        dataItems = [...data.items];
        console.log("🛒 Found", dataItems.length, "items in cart");
      } else {
        console.log("🛒 No items found in cart response");
      }

      console.log("cart data " + JSON.stringify(data, null, 2));

      // Set the cart items in the store
      const cartItems = dataItems.map((item) => ({
        quantity: item?.quantity,
        realTimeTotalPrice: item?.realTimeTotalPrice,
        ...item?.product,
      }));
      
      console.log("🛒 Setting cart items:", cartItems);
      setCart(cartItems);
      
      // Persist cart to localStorage
      localStorage.setItem("cart", JSON.stringify(cartItems));
      console.log("🛒 Cart data saved to localStorage:", cartItems);
    } catch (error) {
      console.error("🛒 Error fetching cart data:", error.message);
      console.error("🛒 Full error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const clearCartHandler = () => {
    setCart([]);
  };

  const placeOrderHandler = async (orderData) => {
    console.log("cart id " + JSON.stringify(orderData, null, 2));
    try {
      const data = await createOrder(orderData);
      console.log("✅ Order response:", data);

      if (orderData.paymentMethod === "ONLINE") {
        const options = {
          key: process.env.RAZORPAY_KEY_ID,
          amount: data.razorpayOrder.amount,
          currency: "INR",
          name: "Jewellery Store",
          description: "Order Payment",
          order_id: data.razorpayOrder.id,
          handler: async function (response) {
            const paymentData = {
              ...response,
              orderId: data.order._id,
            };

            try {
              const verifyResponse = await verifyPayment(paymentData);
              console.log("✅ Payment verified:", verifyResponse);
              alert("Payment successful!");
              clearCartHandler();
            } catch (err) {
              console.error("❌ Payment verification failed:", err.message);
              alert("Payment failed or verification failed.");
            }
          },
          prefill: {
            name: orderData.shippingAddress.fullName,
            email: "mukut@test.com",
            contact: orderData.shippingAddress.phone,
          },
          theme: {
            color: "#3399cc",
          },
        };

        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        alert("Order placed successfully with COD!");
        clearCartHandler();
      }
    } catch (error) {
      console.error("❌ Error placing order:", error.message);
      console.log("🔥 Full error object:", error);
      alert("Failed to place order");
    }
  };

  console.log("cart from context " + JSON.stringify(cart, null, 2));

  const cartContextValues = {
    cart,
    setCart,
    addToCart: addToCartHandler,
    removeSingleItem: removeSingleItemHandler,
    removeFromCart: removeFromCartHandler,
    getCart: getCartHandler,
    clearCart: clearCartHandler,
    placeOrder: placeOrderHandler,
  };

  return (
    <CartContext.Provider value={cartContextValues}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);